import { TelemetryGranularity, TelemetryPeriod } from '@core/constants/telemetry-route.constant';

/**
 * Which day the performance ratios describe, derived from the insights page's selected
 * period.
 *
 * The product spec says the ratios follow "the day selected by the user", but the page's
 * selector only produces a *day* at the two day granularities — at week, month and year
 * granularity the user has picked a period, not a day. The rule applied here is:
 *
 *   the last complete day inside the selected period, or the last complete day overall
 *   when that period has not finished yet.
 *
 * That last clause also covers the common case of a user looking at today: today is still
 * running, so its ratio would be computed over a partial day and read as a collapse.
 *
 * This rule is an assumption, not a specified requirement — which is exactly why it lives
 * in one function instead of being spread across the card. If the answer turns out to be
 * different (for instance "always the last complete day, regardless of selection"), this is
 * the only place that changes.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

// Selector fragments, as built by the time selector: `day-per-hour/2026-08-14`,
// `day-per-quarter-hour/2026-08-14`, `week-per-day/2026/33`, `month-per-day/2026/8`,
// `year-per-month/2026`.
export function anchorDayFor(selectedDate: string, today: Date): Date | null {
  const lastComplete = startOfDay(new Date(today.getTime() - DAY_MS));
  const [granularity, ...parts] = selectedDate.split('/');

  const candidate = periodEnd(granularity, parts);
  if (candidate === null) return null;

  return candidate.getTime() < lastComplete.getTime() ? candidate : lastComplete;
}

// The same rule for a period in the telemetry API's terms. Its week carries the ISO week-year, so
// it needs none of the calendar-year guessing `endOfIsoWeek` does for the string fragments.
export function anchorDayForPeriod(selected: TelemetryPeriod, today: Date): Date | null {
  const lastComplete = startOfDay(new Date(today.getTime() - DAY_MS));

  const candidate = telemetryPeriodEnd(selected.granularity, selected.period.split('/'));
  if (candidate === null) return null;

  return candidate.getTime() < lastComplete.getTime() ? candidate : lastComplete;
}

function telemetryPeriodEnd(granularity: TelemetryGranularity, parts: string[]): Date | null {
  switch (granularity) {
    case 'hour':
    case 'quarter-hour':
      return periodEnd('day-per-hour', parts);
    case 'week': {
      const [weekYear, week] = parts.map(Number);
      if (!Number.isFinite(weekYear) || !Number.isFinite(week)) return null;
      return endOfWeek(isoWeekMonday(weekYear, week));
    }
    case 'day':
      return periodEnd('month-per-day', parts);
    case 'month':
      return periodEnd('year-per-month', parts);
    default:
      return null;
  }
}

function periodEnd(granularity: string, parts: string[]): Date | null {
  switch (granularity) {
    case 'day-per-hour':
    case 'day-per-quarter-hour': {
      const day = parseDate(parts[0]);
      return day === null ? null : startOfDay(day);
    }
    case 'week-per-day': {
      const [year, week] = parts.map(Number);
      if (!Number.isFinite(year) || !Number.isFinite(week)) return null;
      return endOfIsoWeek(year, week);
    }
    case 'month-per-day': {
      const [year, month] = parts.map(Number);
      if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
      // Day 0 of the following month is the last day of this one.
      return new Date(year, month, 0);
    }
    case 'year-per-month': {
      const year = Number(parts[0]);
      return Number.isFinite(year) ? new Date(year, 11, 31) : null;
    }
    default:
      return null;
  }
}

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

// Both producers of a day fragment emit `YYYY-MM-DD`. Handing that to `new Date()` parses it
// as midnight *UTC* per spec, while `startOfDay` immediately reads the local calendar fields —
// so at any negative UTC offset the anchor collapses to the previous day and the card fetches
// and labels the wrong one. Building the date from the parts keeps it local throughout.
function parseDate(value: string | undefined): Date | null {
  if (!value) return null;

  const parts = ISO_DAY.exec(value);
  if (parts !== null) {
    const [year, month, day] = parts.slice(1).map(Number);
    const parsed = new Date(year, month - 1, day);
    // Out-of-range parts roll over silently (month 13 becomes January of the next year), so
    // reject anything that does not come back out the way it went in.
    return parsed.getMonth() === month - 1 && parsed.getDate() === day ? parsed : null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// The Sunday closing the given week.
//
// Neither producer of a week fragment supplies the ISO *week-year*: the time selector emits
// `start.year()` (the calendar year of the week's Monday) beside `start.isoWeek()`, and the
// telemetry-time helper emits `moment().year()` beside `moment().week()`. Those two years
// diverge exactly when week 1 starts in late December — the week of Monday 29 December 2025 is
// ISO week 1 of week-year 2026 but arrives here as `2025/1`. Read as a week-year that resolves
// to 5 January 2025, a full year early, and because a past date passes the "already complete"
// clamp untouched the card would silently show the wrong year's day.
//
// So: take the week-year reading, and if its Monday falls outside the calendar year we were
// handed, prefer the next week-year when that one does put the Monday inside it.
function endOfIsoWeek(year: number, week: number): Date {
  const monday = isoWeekMonday(year, week);
  if (monday.getFullYear() !== year) {
    const nextWeekYear = isoWeekMonday(year + 1, week);
    if (nextWeekYear.getFullYear() === year) return endOfWeek(nextWeekYear);
  }
  return endOfWeek(monday);
}

// The Monday opening the given week of the given ISO week-year. ISO weeks run Monday–Sunday
// and week 1 is the week containing 4 January, so that date anchors the arithmetic.
function isoWeekMonday(weekYear: number, week: number): Date {
  const fourthOfJanuary = new Date(weekYear, 0, 4);
  // getDay() is 0 for Sunday; ISO counts Sunday as 7.
  const isoWeekday = fourthOfJanuary.getDay() || 7;
  return new Date(weekYear, 0, 4 - (isoWeekday - 1) + (week - 1) * 7);
}

function endOfWeek(monday: Date): Date {
  return new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6);
}
